import { AI_ROLE, AI_MESSAGE_TYPE, AI_ASSISTANT_STATUS, TOOL_LABEL_KEYS, MODIFICATION_TOOLS, VerificationStepStatus } from '~/stores/aiAssistantTypes';
import type { AiRole, AiMessage, AiMessageType, AiAssistantStatus, AiFileInfo, AiInteractionType, AiInteractiveOption, FreeLineData, InlineFormKind, VerificationStep, VerificationState, QueuedVerificationEvent } from '~/stores/aiAssistantTypes';
import { parseHistoryMessages } from '~/utils/aiHistoryParser';
import { SPECIAL_SELECTION } from '~/utils/aiEntityMeta';

// Modal:open / modal:close event payload that LayoutModals listens for.
export const AI_ASSISTANT_MODAL_NAME = 'ai:assistant:modal';

// URL query param that drives modal open state and active conversation.
// `?aiAssistant=1` opens a fresh conversation; `?aiAssistant=<uuid>` restores one.
export const AI_ASSISTANT_QUERY_PARAM = 'aiAssistant';

export const useAiAssistantStore = defineStore('AiAssistantStore', () => {
    const { t } = useTranslation();
    const messages = ref<AiMessage[]>([]);
    const status = ref<AiAssistantStatus>(AI_ASSISTANT_STATUS.IDLE);
    const conversationId = ref<string | null>(null);
    const documentType = ref<string | null>(null);
    const documentId = ref<string | null>(null);
    const documentTitle = ref<string | null>(null);
    const currentStreamingMessage = ref<string>('');
    const lastToolName = ref<string | null>(null);
    const errorMessage = ref<string | null>(null);
    const hadModificationTools = ref(false);
    const createdDocumentId = ref<string | null>(null);
    const createdDocumentType = ref<string | null>(null);
    const pendingQuestions = ref<Array<{ type: AiInteractionType; question: string; options: AiInteractiveOption[] }>>([]);
    const batchedSelections = ref<Array<{ question: string; selected: string; options: AiInteractiveOption[]; freeLineData?: FreeLineData | null }>>([]);
    const previewEntity = ref<{ type: string; id: string } | null>(null);
    const verifications = ref<Map<string, VerificationState>>(new Map());
    const pendingStepQueue = ref<QueuedVerificationEvent[]>([]);
    const flushTimer = ref<ReturnType<typeof setInterval> | null>(null);
    const STAGGER_INTERVAL_MS = 400;

    const openPreview = (type: string, id: string) => {
        if (previewEntity.value?.type === type && previewEntity.value?.id === id) {
            previewEntity.value = null;
            return;
        }
        previewEntity.value = { type, id };
    };

    const autoPreview = (type: string, id: string) => {
        if (previewEntity.value?.type === type && previewEntity.value?.id === id) return;
        previewEntity.value = { type, id };
    };

    const closePreview = () => {
        previewEntity.value = null;
    };

    const addMessage = (role: AiRole, content: string, options?: { type?: AiMessageType; documentCreated?: Array<{ type: string; id: string }>; fileInfo?: AiFileInfo; narration?: boolean; bubbleIntro?: string | null; autoFix?: { applied: boolean; dismissed?: boolean } | null }) => {
        messages.value.push({
            id: crypto.randomUUID(),
            role,
            content,
            type: options?.type ?? AI_MESSAGE_TYPE.TEXT,
            documentCreated: options?.documentCreated ?? null,
            timestamp: new Date(),
            fileInfo: options?.fileInfo,
            narration: options?.narration ?? false,
            bubbleIntro: options?.bubbleIntro ?? null,
            autoFix: options?.autoFix ?? null,
        });
    };

    const appendStreamDelta = (delta: string) => {
        currentStreamingMessage.value += delta;
    };

    // Append a separate "inline form" message bubble below an existing disambig. The disambig
    // keeps its options visible (read-only feel) so the user can still pick a different option,
    // and the form lives in its own bubble — no box-in-box.
    const openInlineForm = (parentMessageId: string, kind: InlineFormKind, searchTerm: string) => {
        // Don't stack two open forms for the same parent — replace any existing one.
        closeInlineFormFor(parentMessageId);
        messages.value.push({
            id: crypto.randomUUID(),
            role: AI_ROLE.ASSISTANT,
            content: '',
            type: AI_MESSAGE_TYPE.INLINE_FORM,
            documentCreated: null,
            timestamp: new Date(),
            inlineFormKind: kind,
            inlineFormSearchTerm: searchTerm,
            inlineFormParentId: parentMessageId,
        });
    };

    const closeInlineForm = (formMessageId: string) => {
        messages.value = messages.value.filter((m) => m.id !== formMessageId);
    };

    const closeInlineFormFor = (parentMessageId: string) => {
        messages.value = messages.value.filter(
            (m) => !(m.type === AI_MESSAGE_TYPE.INLINE_FORM && m.inlineFormParentId === parentMessageId),
        );
    };

    const addInteractiveMessage = (data: { type: AiInteractionType; question: string; options: AiInteractiveOption[] }) => {
        if (currentStreamingMessage.value) {
            addMessage(AI_ROLE.ASSISTANT, currentStreamingMessage.value);
            currentStreamingMessage.value = '';
        }
        messages.value.push({
            id: crypto.randomUUID(),
            role: AI_ROLE.ASSISTANT,
            content: data.question,
            type: AI_MESSAGE_TYPE.INTERACTIVE,
            documentCreated: null,
            timestamp: new Date(),
            interactionType: data.type,
            options: data.options,
            optionSelected: false,
        });
    };

    const addBatchedInteractiveMessages = (questions: Array<{ type: AiInteractionType; question: string; options: AiInteractiveOption[] }>) => {
        if (currentStreamingMessage.value) {
            addMessage(AI_ROLE.ASSISTANT, currentStreamingMessage.value);
            currentStreamingMessage.value = '';
        }
        batchedSelections.value = [];
        pendingQuestions.value = questions.slice(1);
        addInteractiveMessage(questions[0]);
    };

    const handleBatchedSelection = (messageId: string, value: string, freeLineData?: FreeLineData): { allDone: boolean; selections: typeof batchedSelections.value } => {
        const msg = messages.value.find((m) => m.id === messageId);
        if (msg) {
            msg.optionSelected = true;
            msg.selectedLabel = value === SPECIAL_SELECTION.FreeLine ? SPECIAL_SELECTION.FreeLine : msg.options?.find((o) => o.value === value)?.label ?? null;
            if (freeLineData) {
                msg.freeLineData = freeLineData;
            }
            batchedSelections.value.push({
                question: msg.content,
                selected: value,
                options: msg.options ?? [],
                freeLineData: freeLineData ?? null,
            });
        }

        if (pendingQuestions.value.length > 0) {
            const next = pendingQuestions.value.shift()!;
            addInteractiveMessage(next);
            return { allDone: false, selections: batchedSelections.value };
        }

        const selections = [...batchedSelections.value];
        batchedSelections.value = [];
        return { allDone: true, selections };
    };

    const markOptionSelected = (messageId: string) => {
        const msg = messages.value.find((m) => m.id === messageId);
        if (msg) {
            msg.optionSelected = true;
        }
    };

    const dismissUnansweredChoices = () => {
        for (const msg of messages.value) {
            if (msg.type === AI_MESSAGE_TYPE.INTERACTIVE && !msg.optionSelected) {
                msg.optionSelected = true;
            }
        }
    };

    const normalizeToolName = (toolName: string): string => {
        return toolName.replace(/^relay__worksite__/, '');
    };

    const addToolCall = (toolName: string) => {
        const normalized = normalizeToolName(toolName);
        lastToolName.value = normalized;
        if (MODIFICATION_TOOLS.has(normalized)) {
            hadModificationTools.value = true;
        }
    };

    const finalizeStream = (documentCreated?: Array<{ type: string; id: string }>, narration: boolean = false, bubbleIntro: string | null = null) => {
        if (currentStreamingMessage.value) {
            addMessage(AI_ROLE.ASSISTANT, currentStreamingMessage.value, {
                documentCreated: documentCreated ?? undefined,
                narration,
                bubbleIntro,
            });
        }
        currentStreamingMessage.value = '';
        lastToolName.value = null;
        status.value = AI_ASSISTANT_STATUS.IDLE;
    };

    const setStatus = (newStatus: AiAssistantStatus) => {
        status.value = newStatus;
        if (newStatus === AI_ASSISTANT_STATUS.PROCESSING) {
            hadModificationTools.value = false;
        }
        if (newStatus !== AI_ASSISTANT_STATUS.ERROR) {
            errorMessage.value = null;
        }
    };

    const setError = (message: string) => {
        status.value = AI_ASSISTANT_STATUS.ERROR;
        errorMessage.value = message;
    };

    const loadHistory = (historyMessages: Array<{ role: 'user' | 'assistant'; content: string; tool_results?: string | object | null; meta?: string | Record<string, any> | null }>) => {
        messages.value = parseHistoryMessages(historyMessages);
    };

    const revokeObjectUrls = () => {
        for (const msg of messages.value) {
            if (msg.fileInfo?.thumbnail) {
                URL.revokeObjectURL(msg.fileInfo.thumbnail);
            }
        }
    };

    const reset = () => {
        revokeObjectUrls();
        messages.value = [];
        status.value = AI_ASSISTANT_STATUS.IDLE;
        conversationId.value = null;
        documentType.value = null;
        documentId.value = null;
        documentTitle.value = null;
        currentStreamingMessage.value = '';
        lastToolName.value = null;
        errorMessage.value = null;
        createdDocumentId.value = null;
        createdDocumentType.value = null;
        pendingQuestions.value = [];
        batchedSelections.value = [];
        previewEntity.value = null;
        verifications.value = new Map();
        pendingStepQueue.value = [];
        if (flushTimer.value) {
            clearInterval(flushTimer.value);
            flushTimer.value = null;
        }
    };

    const isProcessing = computed(() => status.value === AI_ASSISTANT_STATUS.PROCESSING);
    const hasPendingQuestions = computed(() => pendingQuestions.value.length > 0 || batchedSelections.value.length > 0);

    const applyVerificationStep = (documentId: string, step: VerificationStep) => {
        // When a new verification run starts on a doc that was previously completed
        // (e.g. after an auto-fix re-prompt), reset the entry so the card animates
        // from scratch instead of layering a 2nd run over the stale 1st-run state.
        const previous = verifications.value.get(documentId);
        const isFreshRun = step.step === 1 && step.status === VerificationStepStatus.Running && previous?.isComplete;
        const existing = (!previous || isFreshRun)
            ? { steps: [], isComplete: false, finalStatus: null, minimized: false }
            : previous;

        const stepIndex = existing.steps.findIndex(s => s.step === step.step);
        if (stepIndex >= 0) {
            existing.steps[stepIndex] = step;
        } else {
            existing.steps.push(step);
        }

        verifications.value.set(documentId, { ...existing });
    };

    const applyVerificationComplete = (documentId: string, status: 'pass' | 'issues_found') => {
        const existing = verifications.value.get(documentId);
        if (existing) {
            existing.isComplete = true;
            existing.finalStatus = status;
            // A 'pass' verdict is the backend's final word (e.g. after an auto-fix dismissed
            // the only issue). The re-broadcast does not replay step 1, so the isFreshRun
            // guard never resets the entry and stale Issue/Warning steps would keep the card
            // red. Normalise them to Pass so the card reflects the backend decision. We never
            // do this on 'issues_found' — real errors must stay visible.
            if (status === 'pass') {
                existing.steps = existing.steps.map(step =>
                    step.status === VerificationStepStatus.Issue || step.status === VerificationStepStatus.Warning
                        ? { ...step, status: VerificationStepStatus.Pass }
                        : step,
                );
            }
            verifications.value.set(documentId, { ...existing });
        }
    };

    // Drain one event per tick. Without this stagger, the 11 deterministic rules broadcast
    // Running + final status nearly simultaneously and the card jumps from empty to complete.
    const flushOneVerificationEvent = () => {
        const next = pendingStepQueue.value.shift();
        if (!next) {
            if (flushTimer.value) {
                clearInterval(flushTimer.value);
                flushTimer.value = null;
            }
            return;
        }
        if (next.kind === 'step') {
            applyVerificationStep(next.documentId, next.step);
        } else if (next.kind === 'complete') {
            applyVerificationComplete(next.documentId, next.status);
        } else {
            setVerificationAutoFixing(next.documentId, true);
        }
    };

    const ensureFlushTimer = () => {
        if (flushTimer.value) return;
        flushOneVerificationEvent();
        flushTimer.value = setInterval(flushOneVerificationEvent, STAGGER_INTERVAL_MS);
    };

    const updateVerificationStep = (documentId: string, step: VerificationStep) => {
        pendingStepQueue.value.push({ kind: 'step', documentId, step });
        ensureFlushTimer();
    };

    const completeVerification = (documentId: string, status: 'pass' | 'issues_found') => {
        pendingStepQueue.value.push({ kind: 'complete', documentId, status });
        ensureFlushTimer();
    };

    const setVerificationAutoFixing = (documentId: string, value: boolean) => {
        const existing = verifications.value.get(documentId);
        if (existing) {
            verifications.value.set(documentId, { ...existing, autoFixing: value });
        }
    };

    const startVerificationAutoFixing = (documentId: string) => {
        pendingStepQueue.value.push({ kind: 'autofixing', documentId });
        ensureFlushTimer();
    };

    const toggleVerificationMinimized = (documentId: string) => {
        const existing = verifications.value.get(documentId);
        if (existing) {
            verifications.value.set(documentId, { ...existing, minimized: !existing.minimized });
        }
    };

    const minimizeVerification = (documentId: string) => {
        const existing = verifications.value.get(documentId);
        if (existing && ! existing.minimized) {
            verifications.value.set(documentId, { ...existing, minimized: true });
        }
    };

    const statusLabel = computed(() => {
        if (lastToolName.value) {
            const key = TOOL_LABEL_KEYS[lastToolName.value];
            return key ? t(key) : t('ai.chat.processing');
        }
        if (status.value === AI_ASSISTANT_STATUS.PROCESSING) return t('ai.chat.thinking');
        if (status.value === AI_ASSISTANT_STATUS.TRANSCRIBING) return t('ai.chat.transcribing');
        return null;
    });

    return {
        messages,
        status,
        conversationId,
        documentType,
        documentId,
        documentTitle,
        currentStreamingMessage,
        lastToolName,
        errorMessage,
        hadModificationTools,
        createdDocumentId,
        createdDocumentType,
        addMessage,
        addInteractiveMessage,
        addBatchedInteractiveMessages,
        openInlineForm,
        closeInlineForm,
        closeInlineFormFor,
        handleBatchedSelection,
        markOptionSelected,
        dismissUnansweredChoices,
        appendStreamDelta,
        addToolCall,
        finalizeStream,
        setStatus,
        setError,
        reset,
        loadHistory,
        pendingQuestions,
        batchedSelections,
        previewEntity,
        openPreview,
        autoPreview,
        closePreview,
        isProcessing,
        hasPendingQuestions,
        statusLabel,
        verifications,
        updateVerificationStep,
        completeVerification,
        startVerificationAutoFixing,
        setVerificationAutoFixing,
        toggleVerificationMinimized,
        minimizeVerification,
    };
});
